import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Home, ArrowLeft, Compass, Map } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto text-center">
            {/* 404 일러스트 */}
            <div className="mb-8 relative">
              <svg 
                viewBox="0 0 400 300" 
                className="w-full max-w-md mx-auto"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* 배경 */}
                <rect width="400" height="200" fill="#FEF3C7" opacity="0.3"/>
                
                {/* 산길 */}
                <path 
                  d="M0 200 L100 100 L200 150 L300 50 L400 150 L400 300 L0 300 Z" 
                  fill="#D1D5DB"
                  opacity="0.3"
                />
                
                {/* 404 텍스트 */}
                <text 
                  x="200" 
                  y="130" 
                  fontSize="80" 
                  fontWeight="bold"
                  textAnchor="middle"
                  fill="#9CA3AF"
                  opacity="0.5"
                >
                  404
                </text>
                
                {/* 나침반 */}
                <g transform="translate(180, 150)">
                  <circle cx="20" cy="20" r="40" fill="#FEF3C7" />
                  <text 
                    x="20" 
                    y="30" 
                    fontSize="35" 
                    textAnchor="middle"
                    className="select-none"
                    style={{ animation: 'spin 10s linear infinite' }}
                  >
                    🧭
                  </text>
                </g>
                
                {/* 발자국들 */}
                <text x="50" y="220" fontSize="20" opacity="0.3">👣</text>
                <text x="150" y="200" fontSize="20" opacity="0.3">👣</text>
                <text x="250" y="210" fontSize="20" opacity="0.3">👣</text>
                <text x="320" y="190" fontSize="20" opacity="0.3">❓</text>
              </svg>
            </div>

            {/* 메시지 영역 */}
            <div className="space-y-4 mb-8">
              <h1 className="text-4xl font-bold text-gray-900">
                페이지를 찾을 수 없어요
              </h1>
              <p className="text-lg text-gray-600">
                요청하신 둘레길이 지도에 없는 것 같습니다.
              </p>
              <p className="text-gray-500">
                하지만 걱정 마세요! 아래 버튼으로 다시 길을 찾아보세요.
              </p>
            </div>

            {/* 액션 버튼들 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md mx-auto mb-8">
              <Button
                onClick={handleGoBack}
                variant="outline"
                className="flex items-center justify-center gap-2 h-12"
              >
                <ArrowLeft className="h-4 w-4" />
                이전 페이지로
              </Button>
              
              <Link to="/">
                <Button
                  variant="default"
                  className="flex items-center justify-center gap-2 h-12 w-full bg-brand hover:bg-brand-dark"
                >
                  <Home className="h-4 w-4" />
                  홈으로 가기
                </Button>
              </Link>
            </div>

            {/* 추천 링크 */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-left max-w-lg mx-auto">
              <h3 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                <Map className="h-5 w-5" />
                인기 둘레길을 둘러보세요
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link 
                  to="/products"
                  className="text-sm text-green-700 hover:text-green-900 hover:underline flex items-center gap-1"
                >
                  <Compass className="h-3 w-3" />
                  전체 상품 보기
                </Link>
                <Link 
                  to="/my/bookings"
                  className="text-sm text-green-700 hover:text-green-900 hover:underline flex items-center gap-1"
                >
                  <Compass className="h-3 w-3" />
                  내 예약 확인
                </Link>
                <Link 
                  to="/about"
                  className="text-sm text-green-700 hover:text-green-900 hover:underline flex items-center gap-1"
                >
                  <Compass className="h-3 w-3" />
                  서비스 소개
                </Link>
                <Link 
                  to="/contact"
                  className="text-sm text-green-700 hover:text-green-900 hover:underline flex items-center gap-1"
                >
                  <Compass className="h-3 w-3" />
                  고객센터
                </Link>
              </div>
            </div>

            {/* 유머러스한 추가 메시지 */}
            <div className="mt-8 text-gray-400 text-sm">
              <p>💭 "모든 길은 로마로 통한다"고 하지만...</p>
              <p>이 길은 아닌 것 같네요! 😊</p>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default NotFound;
